import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { advisorsList, AdvisorAdsCard } from "@/features/advisors"
import { bumpUsage } from "@/shared/lib/usage-stats"

import CardDecoration from "@/assets/img/today/tarot/card_decoration.webp"
import CardBack from "@/assets/img/today/tarot/card_of_the_day.webp"
import CardDecorationBottom from "@/assets/img/today/tarot/card_decoration_bottom.webp"
import TapTip from "@/assets/svg/today/tap_tip.svg?react"

import { findTarotCard } from "../../config/tarot-data"
import { useAstroToday } from "../../hooks/use-astro-today"
import { useOpenedScreens } from "../../hooks/use-opened-screens"
import { TodayTimer } from "../today-timer"
import { TodayLoader } from "../today-loader"
import { PersonalizedReports } from "../personalized-reports"
import { ADVISOR_PRESETS } from "../../config/advisor-presets"

const SCREEN = "tarot"

/** Card-of-the-day category. Ported from Today/Tarot. */
export function TarotCategory() {
  const navigate = useNavigate()
  const { setSelectedAdvisor, setPreSelectedChatMessage } = useChat()
  const { data, meta, loading } = useAstroToday(["tarot"])
  const { isOpened, open } = useOpenedScreens()

  const advisor = advisorsList[2]
  const card = findTarotCard(data?.tarot?.name)
  const opened = isOpened(SCREEN)

  const goToChat = (message?: string) => {
    if (message) setPreSelectedChatMessage(message)
    setSelectedAdvisor(advisor)
    navigate({ to: WebappRoutes.READINGS })
  }

  if (loading) return <TodayLoader />
  if (!card) return <TodayLoader empty />

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={
          opened
            ? undefined
            : () => {
                open(SCREEN)
                bumpUsage("tarot")
              }
        }
        className="relative mx-4 mb-8 mt-10 flex items-center justify-center"
      >
        <img src={CardDecoration} alt="" className="select-none" />
        <span className="absolute left-1/2 top-1/2 aspect-[11/16] w-[55%] -translate-x-1/2 -translate-y-1/2">
          <img
            src={opened ? card.image : CardBack}
            alt=""
            className="size-full rounded-2xl object-cover animate-in fade-in zoom-in-95 duration-700"
          />
        </span>
      </button>

      {!opened ? (
        <div className="mx-4 flex items-center justify-center gap-3 rounded-2xl bg-purple-light/5 px-4 py-3">
          <TapTip className="h-[42px] w-[35px] shrink-0" />
          <p className="font-body text-sm text-purple-light/70">
            Focus on your question and tap to reveal your personal{" "}
            <span className="font-medium text-purple-light">Card of the Day</span>
          </p>
        </div>
      ) : (
        <div className="w-full animate-in fade-in duration-700">
          <div className="px-4">
            <div className="mb-6 flex items-center justify-between gap-6">
              <h2 className="font-display text-2xl font-medium text-purple-light">{card.name}</h2>
              <span className="flex h-8 min-w-[60px] items-center justify-center rounded-full bg-purple-light/10 px-4 font-display text-base font-medium text-purple-light">
                {card.roman}
              </span>
            </div>
            <p className="font-body text-base text-purple-light/70">{card.description}</p>

            <h3 className="mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
              This card has answers
            </h3>
            <p className="mb-6 font-body text-base text-purple-light/70">
              Love, money, purpose - let an expert reveal what this card means for your life.
            </p>

            <AdvisorAdsCard
              advisor={advisor}
              message={`Welcome. You've drawn the ${card.name} - a card with deep meaning. Which part of your life shall we look into: love, career, or finances?`}
              presets={[...ADVISOR_PRESETS.tarot]}
              onPresetClick={goToChat}
            />
          </div>

          <PersonalizedReports />

          <h3 className="mb-2 mt-10 text-center font-display text-2xl font-medium text-purple-light">
            This card chose you
          </h3>
          <div className="relative flex items-center justify-center py-2">
            <img src={CardDecorationBottom} alt="" className="select-none" />
            <img src={card.image} alt="" className="absolute w-[36%] rounded-2xl" />
          </div>
          <p className="mx-4 mb-4 text-center font-body text-base text-purple-light/70">
            Out of the entire deck, this is{" "}
            <span className="font-medium text-white">the one that turned.</span> The universe rarely
            whispers twice. A message is waiting—are you ready to hear it?
          </p>
          <div className="mb-10 px-4">
            <button
              type="button"
              onClick={() => goToChat()}
              className="flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Ask an Advisor Now
            </button>
          </div>

          {meta && (
            <div className="mx-4 mt-6 flex items-center justify-between gap-4 border-t border-purple-light/20 pt-6">
              <p className="font-body text-base text-purple-light/70">
                Come back tomorrow to uncover your next card
              </p>
              <TodayTimer target={meta.next_sync} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
